import {
  AppBar,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  SvgIcon,
  Tab,
  Tabs,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { enqueueSnackbar, Label, RouterLink, useAppbarStore, useOffSetTop, useResponsive, useUserDetailsStore } from '@zeta-core';
import { MinimiseIcon, ZetaAiIcon, ZetaSvgLogo } from '@zeta-core/assets';
import { getErrorMessage, NotificationService } from '@zeta-services';
import { IGenericErrorResponse, INotificationAppViewResponse, INotificationsAppViewRequest } from '@zeta-types';
import { RouterLinks } from '../../app-route-paths';
import { DASHBOARD_CONFIG } from '../common/constants';
import { ILinkType } from '../common/types';
import AccountPopover from './account-popover';
import NotificationsPopover from './notifications-popover';

interface Props {
  navData?: ILinkType[];
  noData?: boolean;
  hideNavOptions?: boolean;
}
const Header = ({ navData, noData, hideNavOptions }: Props) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const lgUp = useResponsive('up', 'lg');
  const { pathname } = useLocation();
  const offset = useOffSetTop(DASHBOARD_CONFIG.HEADER.DESKTOP_HEIGHT);
  const updateAppbar = useAppbarStore((state) => state?.updateAppbarStore);

  const planningStatus = useUserDetailsStore((state) => state.planningStatus);

  const [notificationsOverview, setNotificationsOverview] = useState<INotificationAppViewResponse | null>(null);

  const linkColor = (active: boolean) => {
    let color;

    if (active) {
      color = theme.palette.primary.light;
    } else if (theme.palette.mode === 'dark') {
      color = theme.palette.text.primary;
    } else {
      color = theme.palette.background.default;
    }

    return color;
  };

  const TABS = [
    {
      value: 'all',
      label: 'All',
      count: notificationsOverview?.appCountInfo?.totalCount || 0,
    },
    {
      value: 'read',
      label: 'Read',
      count: notificationsOverview?.appCountInfo?.readCount || 0,
    },
    {
      value: 'unread',
      label: 'Unread',
      count: notificationsOverview?.appCountInfo?.unreadCount || 0,
    },
  ];

  const [currentTab, setCurrentTab] = useState('all');
  const [markedAsRead, setMarkedAsRead] = useState(false);

  const handleChangeTab = useCallback((event: SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  }, []);

  const renderTabs = (
    <Tabs
      value={currentTab}
      onChange={handleChangeTab}
      variant="fullWidth"
      sx={{
        width: '100%',
        '& .MuiTabs-indicator': {
          backgroundColor: 'transparent',
        },
        '& .Mui-selected': {
          background: theme.palette.primary.contrastText,
          margin: '10px',
          borderRadius: '8px',
          boxShadow: '0px 1px 2px 0px #919EAB29',
          color: theme.palette.primary.main,
        },
      }}
    >
      {TABS.map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
          icon={
            <Label variant={(tab.value === currentTab && 'filled') || 'soft'} color={(tab.value === currentTab && 'primary') || 'default'}>
              {tab.count}
            </Label>
          }
        />
      ))}
    </Tabs>
  );

  const notificationAppViewAPI = useMutation({
    mutationFn: (payload: INotificationsAppViewRequest) => NotificationService().notificationAppView(payload),

    onSuccess: (response: AxiosResponse<INotificationAppViewResponse>) => {
      const { data: responseData } = response;
      setNotificationsOverview(responseData);
    },
    onError: (error: IGenericErrorResponse) => {
      const errorCode = error?.response?.data?.errorCode || 'INTERNAL_SERVER_ERROR';
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  const payload: INotificationsAppViewRequest = {
    status: [],
    isTotalCountRequired: true,
  };

  const fetchData = async () => {
    notificationAppViewAPI.mutateAsync(payload);
  };

  useEffect(() => {
    if (pathname.includes('auth')) {
      return; // Do not call the API if pathname includes 'auth'
    }

    fetchData();
    const intervalId = setInterval(fetchData, 300 * 1000);

    return () => clearInterval(intervalId);
  }, [pathname]); // Add pathname as a dependency to track changes

  useEffect(() => {
    if (markedAsRead) {
      fetchData();
      setMarkedAsRead(false);
    }
  }, [markedAsRead]);

  const displaySidebarIconPaths = pathname.includes(RouterLinks.askZeta);

  return (
    <AppBar
      sx={{
        height: DASHBOARD_CONFIG.HEADER.DESKTOP_HEIGHT,
        zIndex: theme.zIndex.appBar + 1,
        bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.text.primary,
        boxShadow: offset ? theme.shadows[19] : 'none',
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          height: '100%',
          px: { lg: 5 },
        }}
      >
        {lgUp ? null : (
          <IconButton
            sx={{
              ml: 2,
              color: theme.palette.common.white,
              display: displaySidebarIconPaths ? 'block' : 'none',
            }}
            onClick={() => updateAppbar({ sidebarOpen: true })}
          >
            <MinimiseIcon />
          </IconButton>
        )}

        <Stack>{lgUp ? <ZetaSvgLogo /> : null}</Stack>

        <List component={Stack} direction="row" justifyContent="center" width="100%" gap={4} disablePadding>
          {!(noData || hideNavOptions) &&
            navData?.map((item) => {
              const active = pathname === item.path;

              return (
                <ListItem disableGutters disablePadding sx={{ width: 'max-content' }} key={item.path}>
                  <ListItemButton
                    LinkComponent={RouterLink}
                    sx={{
                      gap: 1.2,
                      px: 0.25,
                      py: 0.5,
                      color: linkColor(active),
                      alignItems: 'center',
                      '&:hover': { bgcolor: 'transparent !important' },
                    }}
                    disableGutters
                    disableRipple
                    disableTouchRipple
                    href={item.path}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        color: 'inherit',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <SvgIcon
                        sx={{
                          height: 20,
                          width: 20,
                          display: 'block',
                          color: 'inherit',
                          flexShrink: 0,
                        }}
                      >
                        <item.icon color="currentColor" />
                      </SvgIcon>
                    </ListItemIcon>
                    <ListItemText
                      sx={{ m: 0 }}
                      primary={
                        <Typography variant="body2" sx={{ lineHeight: 1.2, color: 'inherit' }}>
                          {item.title}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
        </List>

        {lgUp && !noData ? (
          <NotificationsPopover
            renderTabs={renderTabs}
            notificationsCount={notificationsOverview?.appCountInfo?.unreadCount}
            currentTab={currentTab}
            setMarkedAsRead={setMarkedAsRead}
          />
        ) : null}

        {lgUp && !noData ? (
          <Tooltip title="Ask Zeta">
            <IconButton
              sx={{
                borderRadius: 3,
                boxShadow: theme.customShadows.primary,
                background: theme.palette.common.white,
                mx: 1,
                p: 0.35,
              }}
              onClick={() => navigate(RouterLinks.askZeta, { state: { source: 'agentic-chat' } })}
            >
              <ZetaAiIcon />
            </IconButton>
          </Tooltip>
        ) : null}

        {lgUp && !noData ? <AccountPopover /> : null}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
